import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, Scale, AlertTriangle, CheckCircle, XCircle, Briefcase, ShieldCheck, Users } from 'lucide-react';

const TermsOfService = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Terms of Service",
      lastUpdated: "Last Updated: January 2026",
      intro: "Welcome to Adidoo. These Terms of Service govern your use of our services and constitute a legally binding agreement between you and Adidoo, a company registered in the Kingdom of Saudi Arabia. By accessing or using our services, you agree to be bound by these terms.",
      sections: [
        {
          icon: Users,
          title: "1. Acceptance of Terms",
          content: [
            "By using Adidoo's services, you confirm that you are at least 18 years old or have obtained parental/guardian consent.",
            "You agree to comply with all applicable Saudi Arabian laws and regulations.",
            "These terms may be updated periodically, and continued use constitutes acceptance of changes.",
            "You are responsible for regularly reviewing these terms."
          ]
        },
        {
          icon: Briefcase,
          title: "2. Services Description",
          content: [
            "Adidoo provides IT solutions including web development, mobile applications, UI/UX design, digital marketing, and cloud solutions.",
            "Services are provided on a project basis or subscription model as agreed in service contracts.",
            "We reserve the right to modify, suspend, or discontinue services with reasonable notice.",
            "Service availability may be subject to maintenance periods and updates."
          ]
        },
        {
          icon: CheckCircle,
          title: "3. User Obligations",
          content: [
            "Provide accurate and complete information when engaging our services.",
            "Maintain the confidentiality of your account credentials.",
            "Not use services for illegal activities or to violate third-party rights.",
            "Not attempt to gain unauthorized access to our systems or networks.",
            "Comply with Saudi Anti-Cyber Crime Law and E-Commerce regulations.",
            "Provide timely feedback and approvals as required for project completion.",
            "Make payments according to agreed terms and schedules."
          ]
        },
        {
          icon: Scale,
          title: "4. Intellectual Property Rights",
          content: [
            "Upon full payment, clients receive ownership of custom-developed deliverables as specified in the service agreement.",
            "Adidoo retains ownership of pre-existing materials, frameworks, and methodologies.",
            "Clients grant Adidoo the right to showcase completed work in portfolios (unless otherwise agreed).",
            "All trademarks, logos, and brand materials remain property of respective owners.",
            "Third-party software and tools are subject to their respective licenses."
          ]
        },
        {
          icon: FileText,
          title: "5. Payment Terms",
          content: [
            "Prices quoted are in Saudi Riyals (SAR) unless otherwise specified.",
            "Payment terms are specified in individual service agreements.",
            "Late payments may incur additional charges as per Saudi commercial law.",
            "All prices are subject to 15% VAT as per Saudi tax regulations.",
            "Refunds are handled according to our refund policy and applicable consumer protection laws.",
            "Payment methods include bank transfer, credit card, and approved digital payment platforms."
          ]
        },
        {
          icon: AlertTriangle,
          title: "6. Limitation of Liability",
          content: [
            "Adidoo's liability is limited to the amount paid for the specific service in question.",
            "We are not liable for indirect, incidental, or consequential damages.",
            "Services are provided 'as is' without warranties beyond those legally required.",
            "We do not guarantee uninterrupted or error-free service operation.",
            "Force majeure events release Adidoo from performance obligations.",
            "Client is responsible for backup of their data and content."
          ]
        },
        {
          icon: XCircle,
          title: "7. Termination",
          content: [
            "Either party may terminate services with 30 days written notice.",
            "Adidoo reserves the right to immediately terminate for breach of terms.",
            "Upon termination, client must pay for all services rendered up to termination date.",
            "Certain obligations (confidentiality, payment) survive termination.",
            "Refunds for terminated projects are handled on a case-by-case basis."
          ]
        },
        {
          icon: ShieldCheck,
          title: "8. Confidentiality",
          content: [
            "Both parties agree to maintain confidentiality of proprietary information.",
            "Confidential information includes business plans, technical data, and customer information.",
            "Confidentiality obligations continue for 3 years after service termination.",
            "Exceptions include information that is publicly available or legally required to be disclosed."
          ]
        },
        {
          icon: Scale,
          title: "9. Dispute Resolution",
          content: [
            "Disputes shall first be resolved through good-faith negotiation.",
            "If negotiation fails, disputes will be resolved through arbitration in Riyadh, Saudi Arabia.",
            "Arbitration shall be conducted in accordance with Saudi Arbitration Law.",
            "The language of arbitration shall be Arabic or English as mutually agreed.",
            "Court proceedings, if necessary, shall be conducted in Saudi courts."
          ]
        },
        {
          icon: FileText,
          title: "10. Governing Law",
          content: [
            "These terms are governed by the laws of the Kingdom of Saudi Arabia.",
            "Compliance with Saudi E-Commerce Law and Consumer Protection regulations.",
            "All parties submit to the jurisdiction of Saudi courts.",
            "Contract interpretation follows Saudi commercial law principles."
          ]
        }
      ],
      additional: {
        title: "Additional Provisions",
        items: [
          {
            subtitle: "Data Protection:",
            text: "We comply with Saudi Personal Data Protection Law. See our Privacy Policy for details."
          },
          {
            subtitle: "Modifications:",
            text: "We reserve the right to modify these terms. Significant changes will be notified via email."
          },
          {
            subtitle: "Severability:",
            text: "If any provision is found invalid, remaining provisions continue in full force."
          },
          {
            subtitle: "Language:",
            text: "In case of conflict between English and Arabic versions, the Arabic version prevails for legal purposes."
          }
        ]
      },
      contact: {
        title: "Contact Information",
        text: "For questions about these Terms of Service:",
        email: "legal@adidoo.com",
        phone: "+966 55 182 6405",
        address: "Legal Department, Adidoo, Riyadh, Saudi Arabia"
      }
    },
    ar: {
      title: "شروط الخدمة",
      lastUpdated: "آخر تحديث: يناير 2026",
      intro: "مرحباً بك في أديدو. تحكم شروط الخدمة هذه استخدامك لخدماتنا وتشكل اتفاقية ملزمة قانونًا بينك وبين أديدو، وهي شركة مسجلة في المملكة العربية السعودية. باستخدام خدماتنا، فإنك توافق على الالتزام بهذه الشروط.",
      sections: [
        {
          icon: Users,
          title: "1. قبول الشروط",
          content: [
            "باستخدام خدمات أديدو، فإنك تؤكد أنك لا تقل عن 18 عامًا أو حصلت على موافقة الوالدين/الوصي.",
            "توافق على الامتثال لجميع القوانين واللوائح السعودية المعمول بها.",
            "قد يتم تحديث هذه الشروط بشكل دوري، والاستخدام المستمر يشكل قبولاً للتغييرات.",
            "أنت مسؤول عن مراجعة هذه الشروط بانتظام."
          ]
        },
        {
          icon: Briefcase,
          title: "2. وصف الخدمات",
          content: [
            "تقدم أديدو حلول تقنية المعلومات بما في ذلك تطوير المواقع، تطبيقات الجوال، تصميم واجهة المستخدم، التسويق الرقمي، والحلول السحابية.",
            "يتم تقديم الخدمات على أساس المشروع أو نموذج الاشتراك كما هو متفق عليه في عقود الخدمة.",
            "نحتفظ بالحق في تعديل أو تعليق أو إيقاف الخدمات مع إشعار معقول.",
            "قد تخضع توفر الخدمة لفترات الصيانة والتحديثات."
          ]
        },
        {
          icon: CheckCircle,
          title: "3. التزامات المستخدم",
          content: [
            "تقديم معلومات دقيقة وكاملة عند استخدام خدماتنا.",
            "الحفاظ على سرية بيانات اعتماد حسابك.",
            "عدم استخدام الخدمات لأنشطة غير قانونية أو انتهاك حقوق الطرف الثالث.",
            "عدم محاولة الوصول غير المصرح به إلى أنظمتنا أو شبكاتنا.",
            "الامتثال لنظام مكافحة جرائم المعلوماتية السعودي ولوائح التجارة الإلكترونية.",
            "تقديم التعليقات والموافقات في الوقت المناسب كما هو مطلوب لإكمال المشروع.",
            "سداد المدفوعات وفقًا للشروط والجداول الزمنية المتفق عليها."
          ]
        },
        {
          icon: Scale,
          title: "4. حقوق الملكية الفكرية",
          content: [
            "عند الدفع الكامل، يحصل العملاء على ملكية المخرجات المطورة خصيصًا كما هو محدد في اتفاقية الخدمة.",
            "تحتفظ أديدو بملكية المواد الموجودة مسبقًا، والأطر، والمنهجيات.",
            "يمنح العملاء أديدو الحق في عرض الأعمال المنجزة في المحافظ (ما لم يتم الاتفاق على خلاف ذلك).",
            "تظل جميع العلامات التجارية والشعارات ومواد العلامة التجارية ملكًا لأصحابها.",
            "تخضع برامج وأدوات الطرف الثالث لتراخيصها الخاصة."
          ]
        },
        {
          icon: FileText,
          title: "5. شروط الدفع",
          content: [
            "الأسعار المعروضة بالريال السعودي (SAR) ما لم يُذكر خلاف ذلك.",
            "يتم تحديد شروط الدفع في اتفاقيات الخدمة الفردية.",
            "قد تتحمل المدفوعات المتأخرة رسومًا إضافية وفقًا للقانون التجاري السعودي.",
            "جميع الأسعار خاضعة لضريبة القيمة المضافة 15٪ وفقًا للوائح الضريبية السعودية.",
            "يتم التعامل مع المبالغ المستردة وفقًا لسياسة الاسترداد وقوانين حماية المستهلك المعمول بها.",
            "تشمل طرق الدفع التحويل البنكي والبطاقة الائتمانية ومنصات الدفع الرقمية المعتمدة."
          ]
        },
        {
          icon: AlertTriangle,
          title: "6. حدود المسؤولية",
          content: [
            "تقتصر مسؤولية أديدو على المبلغ المدفوع للخدمة المحددة المعنية.",
            "نحن غير مسؤولين عن الأضرار غير المباشرة أو العرضية أو التبعية.",
            "يتم توفير الخدمات 'كما هي' دون ضمانات تتجاوز تلك المطلوبة قانونًا.",
            "لا نضمن تشغيل الخدمة دون انقطاع أو خالي من الأخطاء.",
            "أحداث القوة القاهرة تعفي أديدو من التزامات الأداء.",
            "العميل مسؤول عن نسخ احتياطي من بياناته ومحتواه."
          ]
        },
        {
          icon: XCircle,
          title: "7. الإنهاء",
          content: [
            "يجوز لأي من الطرفين إنهاء الخدمات بإشعار كتابي لمدة 30 يومًا.",
            "تحتفظ أديدو بالحق في الإنهاء الفوري في حالة خرق الشروط.",
            "عند الإنهاء، يجب على العميل دفع جميع الخدمات المقدمة حتى تاريخ الإنهاء.",
            "تظل بعض الالتزامات (السرية، الدفع) سارية بعد الإنهاء.",
            "يتم التعامل مع المبالغ المستردة للمشاريع المنتهية على أساس كل حالة على حدة."
          ]
        },
        {
          icon: ShieldCheck,
          title: "8. السرية",
          content: [
            "يوافق الطرفان على الحفاظ على سرية المعلومات الخاصة.",
            "تشمل المعلومات السرية خطط الأعمال والبيانات التقنية ومعلومات العملاء.",
            "تستمر التزامات السرية لمدة 3 سنوات بعد إنهاء الخدمة.",
            "تشمل الاستثناءات المعلومات المتاحة للجمهور أو المطلوب الإفصاح عنها قانونًا."
          ]
        },
        {
          icon: Scale,
          title: "9. حل النزاعات",
          content: [
            "يجب حل النزاعات أولاً من خلال التفاوض بحسن نية.",
            "إذا فشل التفاوض، سيتم حل النزاعات من خلال التحكيم في الرياض، المملكة العربية السعودية.",
            "يتم إجراء التحكيم وفقًا لنظام التحكيم السعودي.",
            "تكون لغة التحكيم العربية أو الإنجليزية كما هو متفق عليه بشكل متبادل.",
            "تُجرى الإجراءات القضائية، إذا لزم الأمر، في المحاكم السعودية."
          ]
        },
        {
          icon: FileText,
          title: "10. القانون الحاكم",
          content: [
            "تخضع هذه الشروط لقوانين المملكة العربية السعودية.",
            "الامتثال لنظام التجارة الإلكترونية السعودي ولوائح حماية المستهلك.",
            "يخضع جميع الأطراف لاختصاص المحاكم السعودية.",
            "يتبع تفسير العقد مبادئ القانون التجاري السعودي."
          ]
        }
      ],
      additional: {
        title: "أحكام إضافية",
        items: [
          {
            subtitle: "حماية البيانات:",
            text: "نحن نلتزم بنظام حماية البيانات الشخصية السعودي. انظر سياسة الخصوصية للحصول على التفاصيل."
          },
          {
            subtitle: "التعديلات:",
            text: "نحتفظ بالحق في تعديل هذه الشروط. سيتم إخطار التغييرات الهامة عبر البريد الإلكتروني."
          },
          {
            subtitle: "قابلية الفصل:",
            text: "إذا وُجد أي حكم غير صالح، تظل الأحكام المتبقية سارية بالكامل."
          },
          {
            subtitle: "اللغة:",
            text: "في حالة التعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية للأغراض القانونية."
          }
        ]
      },
      contact: {
        title: "معلومات الاتصال",
        text: "للأسئلة حول شروط الخدمة هذه:",
        email: "legal@adidoo.com",
        phone: "+966 55 182 6405",
        address: "القسم القانوني، أديدو، الرياض، المملكة العربية السعودية"
      }
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Scale className="w-20 h-20 text-white mx-auto mb-6 animate-bounce" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            {currentContent.title}
          </h1>
          <p className="text-amber-100 text-lg">{currentContent.lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-l-4 border-amber-600">
          <p className="text-gray-700 leading-relaxed text-lg" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.intro}
          </p>
        </div>

        {/* Sections */}
        {currentContent.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg p-8 mb-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <section.icon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 pt-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.title}
              </h2>
            </div>
            <ul className="space-y-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
              {section.content.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Additional Provisions */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-lg p-8 mb-8 border-2 border-amber-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.additional.title}
          </h2>
          <div className="space-y-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.additional.items.map((item, index) => (
              <div key={index}>
                <h3 className="font-semibold text-gray-800 mb-2">{item.subtitle}</h3>
                <p className="text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl shadow-xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.title}
          </h2>
          <p className="mb-6 text-amber-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.text}
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5" />
              <a href={`mailto:${currentContent.contact.email}`} className="hover:underline" dir="ltr">
                {currentContent.contact.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5" />
              <a href={`tel:${currentContent.contact.phone}`} className="hover:underline" dir="ltr">
                {currentContent.contact.phone}
              </a>
            </div>
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 mt-1 flex-shrink-0" />
              <span dir={language === 'ar' ? 'rtl' : 'ltr'}>{currentContent.contact.address}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;